import { CompartmentSelector, LinkProfile, NoMatchStrategy } from '@ekkogmbh/apisdk';
import { ListItemText, MenuItem, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { LabelLinkManager } from 'src/LabelManagement/Components/LabelLinkManager';
import { CoordinateInput } from '../../Common/Components/CoordinateInput';
import { ContentPanel, StepDefinition, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import NamedCoordinateDescription from 'src/Common/Components/Stepper/NameCoordinateDescription';
import { TechnologyPicker } from 'src/Common/Components/Forms/TechnologyPicker';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useTemplateGroups from 'src/LabelManagement/Providers/TemplateGroupsProvider';
import useCompartmentGenerator from 'src/CompartmentManagement/Providers/CompartmentGeneratorProvider';
import { CenteredColumn, ReactivePanelColumn, ReactiveRow } from 'src/Common/Components/Stacks';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';
import { CompartmentSelectorPicker } from 'src/CompartmentSelectorManagement/CompartmentSelectorPicker';
import { CompartmentGeneratorForm } from 'src/CompartmentManagement/Components/CompartmentGeneratorForm';
import { StyledCheckbox } from 'src/Common/Components/Forms/StyledCheckbox';
import usePagination from 'src/Common/Providers/PaginationProvider';
import ObjectField from 'src/Common/Components/DataTable/ObjectField';
import StyledFormAccordion from 'src/Common/Components/Forms/StyledFormAccordion';
import { Check, Warning } from '@mui/icons-material';

interface Props {
  profile?: LinkProfile;
  closeHandler: () => void;
}

export const LinkProfilePanel = ({ profile, closeHandler }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { currentStep, enableNext, enableSave } = usePanel();
  const { templateGroups, templateGroupNames, setTemplateGroups } = useTemplateGroups();
  const { refresh } = usePagination();
  const { fieldTemplates, replacements, fromReplacements } = useCompartmentGenerator();

  const [isSaving, startSaving] = useRequest();

  const [name, setName] = useState<string>('');
  const [technology, setTechnology] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [compartmentSelector, setCompartmentSelector] = useState<CompartmentSelector>();
  const [autoTemplateGroup, setAutoTemplateGroup] = useState<string | undefined>('');
  const [compartmentInputInstructions, setCompartmentInputInstructions] = useState<string>('');
  const [labelInputInstructions, setLabelInputInstructions] = useState<string>('');
  const [isMultiSelect, setMultiSelect] = useState<boolean>(false);
  const [isRemoveObsolete, setRemoveObsolete] = useState<boolean>(false);

  const isCreationWithCoordinate = useMemo(() => fieldTemplates['coordinate'] !== undefined, [fieldTemplates]);

  const hasPopulatedGroup = useMemo(
    () => templateGroupNames.some((groupName) => Object.keys(templateGroups[groupName] ?? {}).length > 0),
    [templateGroupNames, templateGroups],
  );

  const isAllFilled = useMemo(
    () =>
      ![name, technology, coordinate].includes('') &&
      compartmentSelector !== undefined &&
      hasPopulatedGroup &&
      (replacements.length === 0 || isCreationWithCoordinate),
    [name, technology, coordinate, hasPopulatedGroup, compartmentSelector, replacements, isCreationWithCoordinate],
  );

  const isEditMode = profile !== undefined;

  useEffect(() => {
    switch (currentStep) {
      case 0:
        enableNext(![name, coordinate, technology].includes(''));
        break;
      case 1:
        enableNext(hasPopulatedGroup);
        break;
      case 2:
        enableNext(compartmentSelector !== undefined);
    }

    enableSave(isAllFilled);
  }, [currentStep, isAllFilled, hasPopulatedGroup, name, coordinate, technology, compartmentSelector]);

  const reset = useCallback(() => {
    setName(profile?.name ?? '');
    setTechnology(profile?.technology ?? '');
    setCoordinate(profile?.coordinate ?? '');
    setCompartmentSelector(profile?.compartmentSelector);
    setMultiSelect(profile?.multipleCompartmentSelection ?? false);
    setAutoTemplateGroup(profile?.autoTemplateGroup ?? undefined);
    setTemplateGroups(profile?.templateGroups ?? {});
    setCompartmentInputInstructions(profile?.compartmentInputInstructions ?? '');
    setLabelInputInstructions(profile?.labelInputInstructions ?? '');
    setRemoveObsolete(profile?.removeObsoleteCompartments ?? false);

    if (profile?.noMatchStrategy !== undefined && profile.noMatchStrategy.name !== 'null') {
      const coordinateGen = { key: 'coordinate', ...profile.noMatchStrategy.data.coordinate };
      fromReplacements([coordinateGen, ...(profile.noMatchStrategy.data.fields ?? [])]);
    }
  }, [profile]);

  useEffect(() => {
    reset();
  }, [profile]);

  const saveProfile = useCallback(() => {
    const payload: LinkProfile = {
      name,
      technology,
      coordinate,
      labelInputInstructions: labelInputInstructions ?? null,
      compartmentInputInstructions: compartmentInputInstructions ?? null,
      templateGroups,
      compartmentSelector: compartmentSelector!,
      multipleCompartmentSelection: isMultiSelect,
      autoTemplateGroup: autoTemplateGroup === undefined || autoTemplateGroup === '' ? null : autoTemplateGroup,
      removeObsoleteCompartments: isRemoveObsolete,
    };

    if (replacements.length > 0) {
      const noMatchStrategy: NoMatchStrategy = {
        name: 'regex',
        data: {
          coordinate: fieldTemplates['coordinate'],
          fields: replacements.filter((rpl) => rpl.key !== 'coordinate'),
        },
      };
      payload.noMatchStrategy = noMatchStrategy;
    }

    startSaving(
      () => api.saveLinkProfile(payload, true),
      () => {
        closeHandler();
        refresh();
      },
    );
  }, [
    name,
    technology,
    coordinate,
    labelInputInstructions,
    compartmentInputInstructions,
    templateGroups,
    compartmentSelector,
    isMultiSelect,
    autoTemplateGroup,
    isRemoveObsolete,
    replacements,
    fieldTemplates,
  ]);

  const templateGroupOptions = useMemo(
    () =>
      templateGroupNames.map((grp, idx) => (
        <MenuItem key={idx} value={grp}>
          <ListItemText primary={grp} />
        </MenuItem>
      )),
    [templateGroupNames],
  );

  const identifierStep: StepDefinition = useMemo(
    () => ({
      title: 'Link Profile',
      description: <NamedCoordinateDescription name={name} coordinate={coordinate} />,
      contentCallback: () => (
        <ReactivePanelColumn>
          <StyledTextField
            disabled={isEditMode}
            type="text"
            label="Name"
            value={name}
            onChange={(e) => {
              setName(String(e.target.value));
            }}
          />
          <CoordinateInput
            disabled={isEditMode}
            value={coordinate}
            freeText={false}
            onChange={(coordinate) => {
              setCoordinate(coordinate);
            }}
            trailingDelimiter={false}
          />
          <div>
            <TechnologyPicker
              selected={technology}
              coordinate={coordinate}
              onChange={(technology) => {
                setTechnology(technology as string);
              }}
            />
          </div>
        </ReactivePanelColumn>
      ),
    }),
    [isEditMode, name, coordinate, technology],
  );

  const templateGroupStep: StepDefinition = useMemo(
    () => ({
      title: 'Template Groups',
      description: (
        <ObjectField
          fields={[{ key: 'Template Groups', value: templateGroupNames.length, tooltip: templateGroupNames.join(',') }]}
        />
      ),
      contentCallback: () => (
        <CenteredColumn width={'100%'}>
          <LabelLinkManager coordinate={coordinate} />
        </CenteredColumn>
      ),
    }),
    [coordinate, templateGroupNames],
  );

  const configurationStep: StepDefinition = useMemo(
    () => ({
      title: 'Configuration',
      description: (
        <ObjectField
          direction={'column'}
          fields={[
            {
              key: 'Selector',
              value: <NamedCoordinateDescription name={compartmentSelector?.name} />,
            },
            {
              key: 'Default Group',
              value: autoTemplateGroup ?? false,
            },
          ]}
        />
      ),
      contentCallback: () => (
        <ReactivePanelColumn lgWidth={'66%'}>
          <StyledSelectField
            label={'Automatic Template Group'}
            value={autoTemplateGroup ?? ''}
            onChange={(e) => setAutoTemplateGroup(e.target.value ? String(e.target.value) : undefined)}
          >
            <MenuItem key={-1} value={''}>
              <ListItemText primary={'-'} />
            </MenuItem>
            {templateGroupOptions}
          </StyledSelectField>
          <CompartmentSelectorPicker
            coordinate={coordinate}
            selected={compartmentSelector}
            onChange={setCompartmentSelector}
          />
          <StyledFormAccordion label={'Instructions'}>
            <CenteredColumn width={'100%'}>
              <StyledTextField
                type={'text'}
                label={'Label Input Instructions'}
                tooltip={'Instruction text displayed when applying the profile.'}
                value={labelInputInstructions ?? ''}
                onChange={(e) => setLabelInputInstructions(String(e.target.value))}
              />
              <StyledTextField
                type={'text'}
                label={'Compartment Input Instructions'}
                tooltip={'Instruction text displayed when applying the profile.'}
                value={compartmentInputInstructions ?? ''}
                onChange={(e) => setCompartmentInputInstructions(String(e.target.value))}
              />
            </CenteredColumn>
          </StyledFormAccordion>
          <StyledFormAccordion label={'Options'}>
            <ReactiveRow spacing={1}>
              <StyledCheckbox
                label={'Multiple Compartments Selection'}
                tooltip={'Allow choosing when multiple compartments match'}
                value={isMultiSelect}
                onChange={() => setMultiSelect(!isMultiSelect)}
              />
              <StyledCheckbox
                label={'Remove Obsolete Compartments'}
                tooltip={'Delete compartments when the last link is removed'}
                value={isRemoveObsolete}
                onChange={() => setRemoveObsolete(!isRemoveObsolete)}
              />
            </ReactiveRow>
          </StyledFormAccordion>
          <StyledFormAccordion
            label={'Compartment Creation'}
            centerLabel={
              replacements.length > 0 ? (
                isCreationWithCoordinate ? (
                  <Check color={'success'} />
                ) : (
                  <Stack direction={'row'} spacing={2}>
                    <Warning color={'warning'} />
                    <Typography fontWeight={500}>missing coordinate field</Typography>
                  </Stack>
                )
              ) : (
                ''
              )
            }
          >
            {replacements.length === 0 && (
              <CenteredColumn>
                <Typography fontWeight={700}>enabled by adding the coordinate field</Typography>
              </CenteredColumn>
            )}
            <CompartmentGeneratorForm initialFieldInputValue={replacements.length === 0 ? 'coordinate' : ''} />
          </StyledFormAccordion>
        </ReactivePanelColumn>
      ),
    }),
    [
      isCreationWithCoordinate,
      replacements,
      isRemoveObsolete,
      isMultiSelect,
      compartmentInputInstructions,
      labelInputInstructions,
      coordinate,
      compartmentSelector,
      autoTemplateGroup,
    ],
  );

  return (
    <ContentPanel
      isLoading={isSaving}
      initialStep={isEditMode ? 1 : 0}
      steps={[identifierStep, templateGroupStep, configurationStep]}
      onCancel={closeHandler}
      onSave={saveProfile}
      onReset={reset}
      onClose={closeHandler}
    />
  );
};
